import api from './api';

export interface BusinessContact {
  id?: number;
  business_id?: number;
  contact_number?: string;
  email?: string;
  main_address1?: string;
  main_address2?: string;
  branch_address1?: string;
  branch_address2?: string;
  business_license?: boolean;
  able_to_add_more_branches?: boolean;
}

export interface BusinessNature {
  id?: number;
  business_id?: number;
  business_description?: string;
  msic_code_1?: string;
  msic_code_2?: string;
  msic_code_3?: string;
  msic_code_4?: string;
  msic_code_5?: string;
  trademark_logo?: string;
  industry_specific_license?: string;
}

export interface BusinessFinancial {
  id?: number;
  business_id?: number;
  type?: string;
  bank?: string;
  branch_name?: string;
  bank_account?: string;
}

export interface BusinessEmployer {
  id?: number;
  business_id?: number;
  employer_no?: string;
  sst_id?: string;
  sst_password?: string;
  kwsp_id?: string;
  kwsp_password?: string;
  perkeso_id?: string;
  perkeso_password?: string;
  hrdc_id?: string;
  hrdc_password?: string;
  pcb?: string;
}

export interface BusinessDirector {
  id?: number;
  business_id?: number;
  customer_id?: number;
  name?: string;
  identification_no?: string;
  appoint_date?: string;
  resign_date?: string;
  position?: string;
}

export interface BusinessMember {
  id?: number;
  business_id?: number;
  type?: 'customer' | 'business';
  reference_id?: number;
  customer_id?: number;
  name?: string;
  identification_no?: string;
  appoint_date?: string;
  resign_date?: string;
  share?: number;
}

export interface BusinessSupporting {
  id?: number;
  business_id?: number;
  former_name?: string;
  registration_no?: string;
  sst_no?: string;
  tin_no?: string;
  bank?: string;
  main_address?: string;
  trademark_logo?: string;
  industry_specific_license?: string;
  company_chop?: string;
}

export interface Business {
  id: number;
  customer_id: number;
  name: string;
  type?: 'potential' | 'active' | 'inactive';
  former_name?: string;
  registration_no?: string;
  sst_no?: string;
  tin_no?: string;
  entity?: string;
  incorporation_date?: string;
  year_end?: string;
  financial_statement?: string;
  annual_return?: string;
  constitution: boolean;
  common_seal: boolean;
  engagement?: string;
  agent_id?: number;
  staff_id?: number;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: {
    id: number;
    name: string;
  };
  agent?: {
    id: number;
    name: string;
  };
  staff?: {
    id: number;
    name: string;
  };
  contacts?: BusinessContact[];
  natures?: BusinessNature[];
  financials?: BusinessFinancial[];
  employers?: BusinessEmployer[];
  directors?: BusinessDirector[];
  members?: BusinessMember[];
  supportings?: BusinessSupporting[];
}

export interface CreateBusinessData {
  customer_id: number;
  name: string;
  type?: 'potential' | 'active' | 'inactive';
  former_name?: string;
  registration_no?: string;
  sst_no?: string;
  tin_no?: string;
  entity?: string;
  incorporation_date?: string;
  year_end?: string;
  financial_statement?: string;
  annual_return?: string;
  constitution?: boolean;
  common_seal?: boolean;
  engagement?: string;
  agent_id?: number;
  staff_id?: number;
  contacts?: BusinessContact[];
  natures?: BusinessNature[];
  financials?: BusinessFinancial[];
  employers?: BusinessEmployer[];
  directors?: BusinessDirector[];
  members?: BusinessMember[];
  supportings?: BusinessSupporting[];
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const businessService = {
  getAll: async () => {
    const response = await api.get('/businesses');
    return response.data;
  },

  getById: async (id: number): Promise<Business> => {
    const response = await api.get(`/businesses/${id}`);
    return unwrap<Business>(response);
  },

  create: async (data: CreateBusinessData) => {
    const response = await api.post('/businesses', data);
    return unwrap<Business>(response);
  },

  update: async (id: number, data: Partial<CreateBusinessData> | FormData) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      const response = await api.post(`/businesses/${id}`, data);
      return unwrap<Business>(response);
    }
    const response = await api.put(`/businesses/${id}`, data);
    return unwrap<Business>(response);
  },

  delete: async (id: number) => {
    await api.delete(`/businesses/${id}`);
  },
  import: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/businesses/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  downloadImportTemplate: async () => {
    const response = await api.get('/businesses/import/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  approveChangeRequest: async (id: number) => {
    const response = await api.post(`/business-change-requests/${id}/approve`);
    return response.data;
  },

  rejectChangeRequest: async (id: number) => {
    const response = await api.post(`/business-change-requests/${id}/reject`);
    return response.data;
  },

  createChangeRequest: async (data: {
    business_id: number;
    field_name: string;
    old_value: string;
    new_value: string;
    status?: 'pending' | 'approved' | 'rejected';
  }) => {
    const response = await api.post('/business-change-requests', data);
    return response.data;
  },
};
